import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';

const { FiUser, FiPhone, FiMail, FiAlertTriangle, FiInfo } = FiIcons;

// Mobile detection utility
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768); // md breakpoint
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  return isMobile;
};

// Tablet or below (mobile + tablet)
const useIsTabletOrBelow = () => {
  const [ok, setOk] = useState(false);
  React.useEffect(() => {
    const check = () => setOk(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return ok;
};

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    role: 'customer'
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isTabletOrBelow = useIsTabletOrBelow();

  // Reset role to customer if on mobile and role is operator
  React.useEffect(() => {
    if (isMobile && formData.role === 'operator') {
      setFormData(prev => ({
        ...prev,
        role: 'customer'
      }));
    }
  }, [isMobile, formData.role]);

  // On large screens (>=1024), prevent customer registration and switch to operator
  React.useEffect(() => {
    if (!isTabletOrBelow && formData.role === 'customer') {
      setFormData(prev => ({ ...prev, role: 'operator' }));
    }
  }, [isTabletOrBelow, formData.role]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'mobile' ? value.replace(/\D/g, '').slice(0, 10) : value
    }));
  };

  const handleRoleSelect = (role) => {
    setFormData(prev => ({
      ...prev,
      role
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.role === 'customer' && !isTabletOrBelow) {
      toast.error('Customer registration is available only on mobile or tablet devices.');
      return;
    }

    // Mobile operator registration restriction
    if (isMobile && formData.role === 'operator') {
      toast.error('Operator registration is not available on mobile devices. Please use a desktop device.');
      return;
    }

    if (!formData.name || !formData.mobile || !formData.email) {
      toast.error('Please fill all required fields');
      return;
    }

    if (formData.mobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const m10 = String(formData.mobile).replace(/\D/g, '').slice(-10);
      
      console.log('Registration: Starting registration process for mobile:', m10);
      
      // Check for existing users in both tables
      try {
        // Check customers table
        const { data: existingCustomers, error: custErr } = await supabase
          .from('customers_h2a')
          .select('user_id, phone, full_name')
          .order('created_at', { ascending: false });
          
        if (!custErr && existingCustomers) {
          const existingCustomer = existingCustomers.find(c => {
            if (!c.phone) return false;
            const customerPhone = String(c.phone).replace(/\D/g, '').slice(-10);
            return customerPhone === m10;
          });
          
          if (existingCustomer) {
            toast.error('This mobile number is already registered. Please login instead.');
            setLoading(false);
            return;
          }
        }
        
        // Check operators table
        const { data: existingOperators, error: opErr } = await supabase
          .from('operators_a7b5c9')
          .select('user_id, phone, full_name')
          .order('created_at', { ascending: false });
          
        if (!opErr && existingOperators) {
          const existingOperator = existingOperators.find(o => {
            if (!o.phone) return false;
            const operatorPhone = String(o.phone).replace(/\D/g, '').slice(-10);
            return operatorPhone === m10;
          });
          
          if (existingOperator) {
            toast.error('This mobile number is already registered as an operator. Please login instead.');
            setLoading(false);
            return;
          }
        }
      } catch (lookupErr) {
        console.warn('Registration: Mobile uniqueness check failed (non-blocking):', lookupErr);
      }

      // Generate user ID
      const userId = (typeof crypto !== 'undefined' && crypto.randomUUID) 
        ? crypto.randomUUID() 
        : `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
      const regData = {
        id: userId,
        name: formData.name,
        mobile: m10,
        email: formData.email,
        role: formData.role
      };
      
      console.log('Registration: Creating user with data:', regData);
      
      // Store registration data for OTP verification
      localStorage.setItem('registration_data', JSON.stringify(regData));
      
      // Create the user record immediately in Supabase
      try {
        if (regData.role === 'customer') {
          console.log('Registration: Creating customer record in database');
          
          // Minimal payload: let DB defaults handle timestamps/optional columns
          const customerData = {
            user_id: userId,
            full_name: regData.name,
            email: regData.email,
            phone: m10
          };
          
          console.log('Registration: Inserting customer data:', customerData);
          
          const { data: insertedData, error } = await supabase
            .from('customers_h2a')
            .insert(customerData)
            .select();
            
          if (error) {
            console.error('Failed to create customer record during registration:', error);
            console.error('Error details:', {
              message: error.message,
              details: error.details,
              hint: error.hint,
              code: error.code
            });
            
            // Provide more specific error messages
            if (error.code === '23505') {
              toast.error('This account already exists. Please login instead.');
            } else if (error.code === '42P01') {
              toast.error('Database table not found. Please contact support.');
            } else if (error.message.includes('violates')) {
              toast.error('Invalid data provided. Please check your information.');
            } else {
              toast.error(`Registration failed: ${error.message}`);
            }
            
            setLoading(false);
            return;
          }
          
          console.log('Registration: Customer record created successfully:', insertedData);
          
        } else if (regData.role === 'operator') {
          console.log('Registration: Creating operator record in database');
          
          // Minimal payload: avoid missing column errors; DB defaults fill the rest
          const operatorData = {
            user_id: userId,
            full_name: regData.name,
            email: regData.email,
            phone: m10,
            status: 'active',
            operator_code: `OP${Date.now().toString(36).toUpperCase()}`
          };
          
          console.log('Registration: Inserting operator data:', operatorData);
          
          const { data: insertedData, error } = await supabase
            .from('operators_a7b5c9')
            .insert(operatorData)
            .select();
            
          if (error) {
            console.error('Failed to create operator record during registration:', error);
            console.error('Error details:', {
              message: error.message,
              details: error.details,
              hint: error.hint,
              code: error.code
            });
            
            // Provide more specific error messages
            if (error.code === '23505') {
              toast.error('This account already exists. Please login instead.');
            } else if (error.code === '42P01') {
              toast.error('Database table not found. Please contact support.');
            } else if (error.message.includes('violates')) {
              toast.error('Invalid data provided. Please check your information.');
            } else {
              toast.error(`Registration failed: ${error.message}`);
            }
            
            setLoading(false);
            return;
          }
          
          console.log('Registration: Operator record created successfully:', insertedData);
        }
      } catch (dbError) {
        console.error('Database record creation failed during registration:', dbError);
        toast.error('Registration failed due to database error. Please try again.');
        setLoading(false);
        return;
      }
      
      toast.success('Registration successful! Please verify your mobile number');
      navigate('/verify-otp', { 
        state: { 
          mobile: m10, 
          type: 'register', 
          userData: regData 
        } 
      });
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-md w-full"
      >
        <Card className="p-8">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900">Create Account</h2>
            <p className="text-gray-600 mt-2">Join How2Apply to get started</p>
          </div>

          <div className="flex mb-6">
            {isTabletOrBelow && (
              <button
                type="button"
                onClick={() => handleRoleSelect('customer')}
                className={`flex-1 py-3 px-4 rounded-lg border flex items-center justify-center space-x-2 ${
                  formData.role === 'customer'
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300'
                }`}
              >
                <SafeIcon icon={FiUser} className="w-5 h-5" />
                <span>Customer</span>
              </button>
            )}
            {!isMobile && (
              <button
                type="button"
                onClick={() => handleRoleSelect('operator')}
                className={`flex-1 py-3 px-4 rounded-lg border flex items-center justify-center space-x-2 ${
                  formData.role === 'operator'
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300'
                }`}
              >
                <SafeIcon icon={FiUser} className="w-5 h-5" />
                <span>Operator</span>
              </button>
            )}
          </div>

          {/* Mobile Operator Registration Notice - Only show on desktop */}
          {!isMobile && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-600 mt-0.5" />
                </div>
                <div className="ml-2">
                  <p className="text-sm text-blue-800">
                    <strong>Available Roles:</strong> Customer registration for services, or Operator registration for service providers.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Full Name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              required
            />

            <Input
              label="WhatsApp Mobile Number"
              name="mobile"
              type="tel"
              value={formData.mobile}
              onChange={handleChange}
              placeholder="Enter your 10-digit mobile number"
              required
              maxLength={10}
            />

            <Input
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email address"
              required
            />

            <Button
              type="submit"
              loading={loading}
              className="w-full bg-gradient-to-r from-primary-500 to-blue-600"
              size="lg"
            >
              Register & Get OTP
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                Login here
              </Link>
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center">
              <span className="text-sm text-gray-500">
                By creating an account, you agree to our{' '}
                <a href="#" className="text-primary-600 hover:text-primary-700">
                  Terms
                </a>{' '}
                and{' '}
                <a href="#" className="text-primary-600 hover:text-primary-700">
                  Privacy Policy
                </a>
              </span>
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default RegisterPage;